import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { classifyType } from '@/lib/values';
import { formatCount, formatPercent } from '@/lib/format';
import type { ColumnSchema } from '@/types/api';
import type { GridMode } from '@/cache/types';
import type { ColumnProfile } from '@/cache/stats';
import { EmptyState, LoadingState } from '@/components/common/states';
import { Sparkline } from './Sparkline';
import { ColumnDetail } from './ColumnDetail';

const TYPE_BADGE: Record<string, string> = {
  number: '#',
  bigint: '#',
  boolean: '01',
  temporal: '◷',
  nested: '{}',
  binary: '0x',
  string: 'A',
};

/**
 * Rows profiled beyond those actually on screen.
 *
 * The list is virtualized, so profiles fire only for visible rows plus this
 * much lookahead. That removes the old `profiles x columns` blow-up -- a
 * completed profile used to re-render all 595 rows, which cost ~80 seconds of
 * stalled UI on the flagship table. With virtualization each re-render touches
 * a viewport's worth of rows, so there is no longer a reason to cap the total.
 */
export const PROFILE_LOOKAHEAD = 8;

/** Row height in px, mirrored from `.spark-row`. */
const ROW_HEIGHT = 32;

export interface ColumnExplorerProps {
  readonly columns: readonly ColumnSchema[];
  readonly profiles: Readonly<Record<string, ColumnProfile>>;
  readonly mode: GridMode;
  readonly focusedColumn: string | null;
  readonly onRequestProfile: (column: ColumnSchema) => void;
  readonly onFocusColumn: (name: string | null) => void;
  readonly onUsePredicate: (predicate: string) => void;
}

export function ColumnExplorer({
  columns,
  profiles,
  mode,
  focusedColumn,
  onRequestProfile,
  onFocusColumn,
  onUsePredicate,
}: ColumnExplorerProps): JSX.Element {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? columns.filter((column) => column.name.toLowerCase().includes(needle)) : columns;
  }, [columns, search]);

  const listRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: PROFILE_LOOKAHEAD,
  });

  const virtualRows = virtualizer.getVirtualItems();

  // Profile only what is on screen (plus the virtualizer's overscan). This is
  // what makes a 595-column result cheap: work is proportional to the viewport,
  // not to the column count.
  const firstVisible = virtualRows[0]?.index ?? 0;
  const lastVisible = virtualRows[virtualRows.length - 1]?.index ?? -1;

  useEffect(() => {
    if (mode !== 'local' || lastVisible < firstVisible) return;
    for (let index = firstVisible; index <= lastVisible; index += 1) {
      const column = filtered[index];
      if (column && !profiles[column.name]) onRequestProfile(column);
    }
  }, [filtered, profiles, mode, onRequestProfile, firstVisible, lastVisible]);

  const focused = focusedColumn ? columns.find((column) => column.name === focusedColumn) : undefined;
  const focusedProfile = focusedColumn ? profiles[focusedColumn] : undefined;

  if (focused && focusedProfile) {
    return (
      <ColumnDetail
        column={focused}
        profile={focusedProfile}
        onBack={() => onFocusColumn(null)}
        onUsePredicate={onUsePredicate}
      />
    );
  }

  if (focused && mode === 'local') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="panel-head">
          <button type="button" className="icon-btn" onClick={() => onFocusColumn(null)} aria-label="Back to all columns">
            ←
          </button>
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{focused.name}</span>
        </div>
        <LoadingState label="Profiling column" />
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="panel-head">
          <span className="eyebrow">Columns</span>
        </div>
        <EmptyState title="No result yet" hint="Run a cell to profile its columns." />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="column-explorer">
      <div className="panel-head">
        <span className="eyebrow">Columns</span>
        <span className="tnum text-[10px] text-faint">{columns.length}</span>
      </div>

      <div className="border-b border-line p-1.5">
        <input
          className="field"
          type="search"
          placeholder="Filter columns"
          aria-label="Filter columns"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {mode !== 'local' ? (
        <p className="border-b border-line bg-remote-dim px-2.5 py-1.5 text-[10px] leading-4 text-remote">
          Statistics need the in-browser cache. This result is served from the server, so
          per-column distributions are unavailable.
        </p>
      ) : null}

      <div
        ref={listRef}
        className="min-h-0 flex-1 overflow-auto"
        role="list"
        aria-label={`${filtered.length} columns`}
        data-testid="column-list"
      >
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
        {virtualRows.map((virtualRow) => {
          const column = filtered[virtualRow.index];
          if (!column) return null;
          const profile = profiles[column.name];
          const badge = TYPE_BADGE[classifyType(column.type)] ?? 'A';
          const pending = mode === 'local';

          return (
            <button
              key={column.name}
              type="button"
              role="listitem"
              className="spark-row"
              style={{
                position: 'absolute',
                top: virtualRow.start,
                left: 0,
                width: '100%',
              }}
              aria-selected={focusedColumn === column.name}
              onClick={() => {
                if (!profile) onRequestProfile(column);
                onFocusColumn(column.name);
              }}
              title={`${column.name} · ${column.typeText}`}
            >
              <span className="eyebrow text-faint" aria-hidden="true">
                {badge}
              </span>

              <span className="min-w-0">
                <span className="block truncate font-mono text-[11px] text-ink">{column.name}</span>
                <span className="block truncate text-[10px] text-faint">
                  {profile
                    ? `${formatCount(profile.distinct)} distinct`
                    : column.typeText.toLowerCase()}
                </span>
              </span>

              <span className="flex justify-end">
                {profile ? (
                  <Sparkline profile={profile} />
                ) : pending ? (
                  <span className="h-[18px] w-[76px] animate-pulse rounded-sm bg-line/60" />
                ) : (
                  <span className="text-[10px] text-faint">profile</span>
                )}
              </span>

              <span className="text-right">
                {profile && profile.nullCount > 0 ? (
                  <span className="tnum text-[10px] text-remote" title={`${profile.nullCount} NULLs`}>
                    {formatPercent(profile.nullFraction, 0)}
                  </span>
                ) : profile ? (
                  <span className="text-[10px] text-faint">—</span>
                ) : null}
              </span>
            </button>
          );
        })}
        </div>

        {filtered.length === 0 ? (
          <p className="p-4 text-center text-xs text-muted">No column matches “{search}”.</p>
        ) : null}
      </div>
    </div>
  );
}
